import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

function formatErr(detail) {
  if (!detail) return "Login failed. Please check your credentials.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLoginPage() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user?.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        toast.error("Access denied. Admin account required.");
        return;
      }
      toast.success(`Welcome back, ${u.name}`);
      navigate("/admin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-[calc(100vh-72px)] bg-navy-900 grid place-items-center px-6 py-16">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 lg:p-10 shadow-2xl">
        <p className="overline mb-3">Admin Access</p>
        <h1 className="font-serif text-3xl text-navy-800">Sign in to CRETRE</h1>
        <p className="mt-2 text-sm text-muted-foreground">Manage jobs, enquiries and applications.</p>
        <form onSubmit={submit} className="mt-8 grid gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-navy-800 font-semibold">Email</Label>
            <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className="mt-1.5 h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-navy-800 font-semibold">Password</Label>
            <Input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" className="mt-1.5 h-11" />
          </div>
          <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="mt-2 h-12 bg-navy-800 hover:bg-navy-700 text-white rounded-full">
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
      </div>
    </div>
  );
}
